import React, { useCallback, useMemo } from 'react';

import { Role } from '@ndvr/auth-api';

import { titleCase } from 'utils';
import { Form, FormToggle, Text } from 'widgets';

import { useActiveRoles, useAssignRole, useRemoveRole } from './role-queries';

const EXCLUDED_ROLES = [Role.AUTHENTICATED, Role.EMAIL_SENDER, Role.UNKNOWN];
const ASSIGNABLE_ROLES = Object.values(Role)
    .filter((role) => !role.endsWith('_SVC') && !EXCLUDED_ROLES.includes(role))
    .sort();

export const RoleManagement = ({ userGUID }: { userGUID: string }) => {
    const { data: activeRoles } = useActiveRoles(userGUID);
    const {
        mutate: assignRole,
        isPending: assignmentLoading,
        isError: isRoleAssignmentFailed,
    } = useAssignRole(userGUID);
    const { mutate: removeRole, isPending: removalLoading, isError: isRoleRemovalFailed } = useRemoveRole(userGUID);

    const defaultValues: { [key in Role]?: boolean } = useMemo(() => {
        return (
            ASSIGNABLE_ROLES.reduce((result, role) => {
                const isActive = activeRoles?.some((assignedRole) => assignedRole.role === role);
                return {
                    ...result,
                    [role]: !!isActive,
                };
            }, {}) ?? {}
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeRoles, isRoleAssignmentFailed, isRoleRemovalFailed]);

    const onSubmit = useCallback(
        (data: { [key in Role]?: boolean }) => {
            const [changedRole, isEnabled] =
                Object.entries(data).find(([role, status]) => defaultValues[role as Role] !== status) || [];

            if (isEnabled) {
                assignRole(changedRole as Role);
            } else {
                removeRole(changedRole as Role);
            }
        },
        [assignRole, defaultValues, removeRole],
    );

    const isLoading = assignmentLoading || removalLoading;
    return <RoleForm defaultValues={defaultValues} onSubmit={onSubmit} loading={isLoading} />;
};

const RoleForm = ({
    loading,
    defaultValues,
    onSubmit,
}: {
    loading: boolean;
    defaultValues: { [key: string]: boolean };
    onSubmit: (data: any) => void;
}) => (
    <Form labelWidth="300px" defaultValues={defaultValues} submitOnChange onSubmit={onSubmit} isLoading={loading}>
        <div className="grid grid-cols-2 gap-3">
            {ASSIGNABLE_ROLES.map((role) => (
                <div key={role} className="flex items-center gap-2">
                    <FormToggle hideLabel label={role} name={role} />
                    <Text>{titleCase(role)}</Text>
                </div>
            ))}
        </div>
    </Form>
);
